// CommonJS on purpose: webpack (Gatsby) imports it fine, and the functions
// test suite requires it directly for the cross-runtime parity check
// (dioscuro/functions/lib/__tests__/learningPathParity.test.js).

function stepDone(step, sources) {
  const { labProgress, completedModuleIds, certProofs } = sources || {};
  if (!step || !step.refId) return false;
  switch (step.type) {
    case "lab":
      return Boolean(labProgress && labProgress[step.refId] && labProgress[step.refId].verified === true);
    case "module":
      return Array.isArray(completedModuleIds) && completedModuleIds.includes(step.refId);
    case "cert":
      return Boolean(certProofs && certProofs[step.refId] && certProofs[step.refId].status === "achieved");
    default:
      return false;
  }
}

function resolvePathProgress(pathDef, sources) {
  const levelsIn = (pathDef && Array.isArray(pathDef.levels)) ? pathDef.levels : [];
  let doneCount = 0;
  let total = 0;
  const completedLevelIndexes = [];

  const levels = levelsIn.map((level, idx) => {
    const stepsIn = Array.isArray(level.steps) ? level.steps : [];
    let levelDone = 0;
    const steps = stepsIn.map((s) => {
      const done = stepDone(s, sources || {});
      if (done) levelDone += 1;
      return { type: s.type, refId: s.refId, title: s.title, done };
    });
    doneCount += levelDone;
    total += steps.length;
    const levelPct = steps.length ? Math.round((levelDone / steps.length) * 100) : 0;
    if (steps.length > 0 && levelDone === steps.length) completedLevelIndexes.push(idx);
    return { title: level.title, steps, doneCount: levelDone, total: steps.length, pct: levelPct };
  });

  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  return { levels, doneCount, total, pct, complete: total > 0 && doneCount === total, completedLevelIndexes };
}

function pathMilestoneEvents(resolved, pathId, completionBadgeId, alreadyKeys) {
  const already = alreadyKeys || new Set();
  const out = [];
  for (const idx of resolved.completedLevelIndexes) {
    const key = `path.level_completed:${pathId}:${idx}`;
    if (!already.has(key)) out.push({ event: "path.level_completed", payload: { pathId, levelIndex: idx } });
  }
  if (resolved.complete) {
    const key = `path.completed:${pathId}`;
    if (!already.has(key)) out.push({ event: "path.completed", payload: { pathId, completionBadgeId: completionBadgeId || null } });
  }
  return out;
}

const STEP_TYPES = ["lab", "module", "cert"];

function _pathErr(message) {
  const err = new Error(message);
  err.code = "invalid-argument";
  return err;
}

function _str(v, max, label) {
  if (typeof v !== "string" || !v.trim()) throw _pathErr(`${label} required`);
  if (v.length > max) throw _pathErr(`${label} too long`);
  return v.trim();
}

function validatePathDef(input) {
  const def = input || {};
  const title = _str(def.title, 120, "title");
  const summary = typeof def.summary === "string" ? def.summary.slice(0, 500) : "";
  if (!Array.isArray(def.levels) || def.levels.length < 1 || def.levels.length > 10) {
    throw _pathErr("levels must be 1–10");
  }
  const levels = def.levels.map((lv) => {
    const lvTitle = _str(lv && lv.title, 80, "level title");
    if (!Array.isArray(lv.steps) || lv.steps.length < 1 || lv.steps.length > 20) {
      throw _pathErr("each level needs 1–20 steps");
    }
    const steps = lv.steps.map((s) => {
      if (!s || !STEP_TYPES.includes(s.type)) throw _pathErr("invalid step type");
      return { type: s.type, refId: _str(s.refId, 200, "step refId"), title: _str(s.title, 120, "step title") };
    });
    return { title: lvTitle, steps };
  });
  const completionBadgeId = (typeof def.completionBadgeId === "string" && def.completionBadgeId) ? def.completionBadgeId : null;
  return { title, summary, levels, completionBadgeId };
}

// A learning path may only be assigned to a junior — a user whose tags include
// "junior". Shared by the client (filters the assign dropdown) and the server
// (assignLearningPath enforces it) so the two can't drift.
function isJuniorUser(userData) {
  return Boolean(userData && Array.isArray(userData.tags) && userData.tags.includes("junior"));
}

// "junior-devops" -> "devops"; unknown / non-matching -> null.
function deriveTrack(pathId) {
  const m = /^junior-(devops|sre|platform)$/.exec(String(pathId || ""));
  return m ? m[1] : null;
}

module.exports = { resolvePathProgress, stepDone, validatePathDef, pathMilestoneEvents, STEP_TYPES, isJuniorUser, deriveTrack };
